import APIServices from 'services/APIServices';

const OAUTH_POPUP_NAME = 'slack-activity-oauth';
const OAUTH_POPUP_FEATURES = 'popup=yes,width=600,height=720';

const getResponseData = (response) => response?.data || response;

export const connectSlackActivity = async ({ projectId } = {}) => {
  const popup = window.open('', OAUTH_POPUP_NAME, OAUTH_POPUP_FEATURES);
  if (!popup) {
    throw new Error('Allow popups for this site to connect Slack.');
  }

  try {
    const endpoint = projectId ? `/slack-activity/project/${projectId}/auth-url` : '/slack-activity/auth-url';
    const response = await APIServices.post(endpoint);
    const data = getResponseData(response);

    if (response?.status >= 400 || !data?.authorizationUrl) {
      throw new Error(data?.message || 'Unable to start Slack authorization.');
    }

    popup.location.href = data.authorizationUrl;

    await new Promise((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        cleanup();
        reject(new Error('Slack authorization timed out.'));
      }, 5 * 60 * 1000);
      const pollId = window.setInterval(() => {
        if (popup.closed) {
          cleanup();
          reject(new Error('Slack authorization was cancelled.'));
        }
      }, 500);

      const cleanup = () => {
        window.clearTimeout(timeoutId);
        window.clearInterval(pollId);
        window.removeEventListener('message', handleMessage);
      };

      const handleMessage = (event) => {
        if (event.source !== popup) return;
        if (event.data?.type !== 'slack-activity-oauth') return;

        cleanup();
        if (event.data.success) {
          resolve();
        } else {
          reject(new Error(event.data.message || 'Slack authorization failed.'));
        }
      };

      window.addEventListener('message', handleMessage);
    });
  } catch (error) {
    if (!popup.closed) popup.close();
    throw error;
  }
};

export const disconnectSlackActivity = async () => {
  const response = await APIServices.delete('/slack-activity/disconnect');
  const data = getResponseData(response);

  if (response?.status >= 400) {
    throw new Error(data?.message || 'Unable to disconnect Slack.');
  }

  return data;
};

export const getProjectSlackActivityStatus = async (projectId) => {
  const response = await APIServices.get(`/slack-activity/project/${projectId}/status`);
  const data = getResponseData(response);

  if (response?.status >= 400) {
    throw new Error(data?.message || 'Unable to fetch project Slack status.');
  }

  return data;
};

export const disconnectProjectSlackActivity = async (projectId) => {
  const response = await APIServices.delete(`/slack-activity/project/${projectId}/disconnect`);
  const data = getResponseData(response);

  if (response?.status >= 400) {
    throw new Error(data?.message || 'Unable to disconnect project Slack.');
  }

  return data;
};

export const dismissProjectSlackBanner = async (projectId) => {
  const response = await APIServices.patch(`/slack-activity/project/${projectId}/banner-dismiss`);
  const data = getResponseData(response);

  if (response?.status >= 400) {
    throw new Error(data?.message || 'Unable to dismiss project Slack banner.');
  }

  return data;
};
